// src/components/BookCover/CoverTemplatesModal.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Skeleton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LayersIcon from '@mui/icons-material/Layers';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { API_BASE } from '../../utils/api';

const resolveCoverUrl = (val) => {
  const v = (val || '').trim();
  if (!v) return null;
  if (v.startsWith('http://') || v.startsWith('https://')) return v;
  if (v.startsWith('/')) return `${API_BASE}${v}`;
  if (v.startsWith('covers/')) return `${API_BASE}/${v}`;
  return `${API_BASE}/static/images/book-covers/${v}`;
};

/**
 * CoverTemplatesModal
 *
 * Shows the author's purchased covers as selectable templates.
 * Props:
 * - open: Boolean
 * - onClose: () => void
 * - onSelectTemplate: (templateId: string) => void — called when user clicks "Use This Template"
 * - isCreatingVersion: Boolean
 */
export default function CoverTemplatesModal({ open, onClose, onSelectTemplate, isCreatingVersion = false }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const t = localStorage.getItem('rv_access_token');
      const res = await fetch(`${API_BASE}/api/my/covers`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load purchased templates');
      const data = await res.json();
      const templateList = Array.isArray(data) ? data : (data.covers || []);
      setTemplates(templateList);
    } catch (err) {
      setError('Could not load your purchased templates. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      setSelectedId(null);
    }
  }, [open, fetchTemplates]);

  const handleConfirm = () => {
    if (!selectedId) return;
    onSelectTemplate(selectedId);
  };

  const handleGoToMarket = () => {
    onClose();
    navigate('/library', { state: { initialView: 'coverMarket' } });
  };

  const handleDownloadMetadata = (template) => {
    const metadata = {
      id: template._id || template.id,
      title: template.title || template.name,
      designerName: template.designerName || template.designer || 'Unknown',
      coverType: template.coverType,
      tags: template.tags || [],
      price_cents: template.price_cents,
      currency: template.currency,
      dimensions: template.dimensions,
      description: template.description,
      createdAt: template.createdAt,
      updatedAt: template.updatedAt,
    };
    const dataStr = JSON.stringify(metadata, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${metadata.title || 'template'}-metadata.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={isCreatingVersion ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#413B8D',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LayersIcon />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            My Purchased Templates
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={onClose}
            disabled={isCreatingVersion}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select one of the cover templates you've purchased to start a new version from it.
        </Typography>

        {loading && (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <Grid item xs={12} sm={6} md={4} key={n}>
                <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                <Skeleton variant="text" sx={{ mt: 1 }} width="60%" />
                <Skeleton variant="text" width="40%" />
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && error && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
            <Button variant="outlined" onClick={fetchTemplates}>Retry</Button>
          </Box>
        )}

        {!loading && !error && templates.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 4,
              backgroundColor: '#f0eeff',
              borderRadius: 3,
              border: '2px dashed #9e96d8',
            }}
          >
            <LayersIcon sx={{ fontSize: 56, color: '#9e96d8', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#413B8D', fontWeight: 600, mb: 1 }}>
              No Purchased Templates Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Visit the Cover Market to browse and purchase templates from designers.
            </Typography>
            <Button
              variant="contained"
              startIcon={<StorefrontIcon />}
              onClick={handleGoToMarket}
              sx={{ backgroundColor: '#413B8D', '&:hover': { backgroundColor: '#322d6e' } }}
            >
              Go to Cover Market
            </Button>
          </Box>
        )}

        {!loading && !error && templates.length > 0 && (
          <Grid container spacing={2}>
            {templates.map((tmpl) => {
              const id = tmpl._id || tmpl.id;
              const isSelected = selectedId === id;
              const preview = tmpl.cover_url || tmpl.previewImage || tmpl.coverImage || tmpl.coverUrl || tmpl.preview || tmpl.thumbnail;
              const title = tmpl.title || tmpl.name || 'Untitled';
              return (
                <Grid item xs={12} sm={6} md={4} key={id}>
                  <Box
                    onClick={() => !isCreatingVersion && setSelectedId(id)}
                    sx={{
                      cursor: isCreatingVersion ? 'not-allowed' : 'pointer',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: isSelected ? '2px solid #D4AF37' : '1px solid #e0dff4',
                      backgroundColor: 'white',
                      position: 'relative',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      '&:hover': { boxShadow: '0 4px 16px rgba(65,59,141,0.18)' },
                    }}
                  >
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadMetadata(tmpl);
                      }}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        '&:hover': { backgroundColor: 'white' },
                        zIndex: 1,
                      }}
                      title="Download metadata"
                    >
                      <FileDownloadIcon sx={{ fontSize: 18, color: '#413B8D' }} />
                    </IconButton>
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          color: '#D4AF37',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          zIndex: 1,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        height: 200,
                        backgroundColor: '#f0eeff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {preview ? (
                        <img
                          src={resolveCoverUrl(preview)}
                          alt={title}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <LayersIcon sx={{ fontSize: 64, color: '#9e96d8' }} />
                      )}
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}
                        noWrap
                      >
                        {title}
                      </Typography>
                      {tmpl.designerName && (
                        <Typography variant="caption" color="text.secondary">
                          by {tmpl.designerName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0dff4', justifyContent: 'space-between' }}>
        <Button
          onClick={handleGoToMarket}
          disabled={isCreatingVersion}
          startIcon={<StorefrontIcon />}
          variant="outlined"
          sx={{ borderColor: '#413B8D', color: '#413B8D' }}
        >
          Go to Cover Market
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={isCreatingVersion} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedId || isCreatingVersion}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#1a1a1a',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#b8972e' },
              '&:disabled': { backgroundColor: '#e7dba0', color: '#999' },
            }}
          >
            {isCreatingVersion ? <CircularProgress size={18} sx={{ color: '#999' }} /> : 'Use This Template'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
